import axios from "axios";
import { useStore } from "vuex";

export default function useTracking() {
  const store = useStore();
  const base_url = store.state.baseUrl;

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const requestWithRetry = async (
    url,
    data = {},
    method = "patch",
    retries = 3,
    delayTime = 1000
  ) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        if (method === "patch") {
          await axios.patch(url, data);
        } else if (method === "post") {
          await axios.post(url, data);
        }
        return;
      } catch (error) {
        if (
          error.response &&
          error.response.status === 429 &&
          attempt < retries
        ) {
          console.warn(
            `Retrying request due to rate limit (attempt ${attempt})...`
          );
          await delay(delayTime);
          delayTime *= 2; // Exponential backoff
        }
        if (error.response && error.response.status === 403) {
          console.log("Ad budget exhausted and deactivated");
        } else {
          console.error(`Error making request to ${url}:`, error);
          return;
        }
      }
    }
  };

  const incrementMetric = async (propertyId, metric) => {
    await requestWithRetry(
      `${base_url}properties/${propertyId}/metrics/increment`,
      { metric },
      "post"
    );
  };

  const updateMetric = async (propertyId, metric, value) => {
    await requestWithRetry(
      `${base_url}properties/${propertyId}/metrics/update`,
      { metric, value },
      "post"
    );
  };

  const incrementPropertyViews = async (id, metrics = {}) => {
    try {
      await axios.patch(`${base_url}properties/${id}/views`, metrics);
    } catch (error) {
      console.error(error);
    }
  };

  const incrementPropertyClicks = async (id, metrics = {}) => {
    try {
      await axios.patch(`${base_url}properties/${id}/clicks`, metrics);
    } catch (error) {
      console.error(error);
    }
  };
  const recordConversion = async (propertyId, userId, conversionType) => {
    await requestWithRetry(`${base_url}properties/${propertyId}/conversions`, {
      user_id: userId,
      property_id: propertyId,
      conversion_type: conversionType,
    });
  };

  const recordPageView = async (propertyId, routeName) => {
    await requestWithRetry(
      `${base_url}properties/page-view`,
      {
        property_id: propertyId,
        page_name: routeName,
      },
      "post"
    );
  };

  return {
    incrementMetric,
    updateMetric,
    incrementPropertyViews,
    incrementPropertyClicks,
    recordConversion,
    recordPageView,
  };
}
